import { EventBus } from '@/plugins/event-bus'
import store from '@/store/store'
import Customers from '@/api/server/Customers'
import Companies from '@/api/server/Companies'
import FleetSalesContracts from '@/api/server/FleetSalesContracts'
import PartnerQuote from '@/api/server/PartnerQuote.api'
import moment from 'moment'
import CustomerSearchType from '@/helpers/enums/customerSearchType.js'
import { extend } from 'lodash'

const PrefillService = {
  // returns new QuoteId if new quote had to be created
  async prefill(routeParams, query, dealerSettings) {
    if (query.customerpoid) {
      await this._prefillNationalCustomer(query.customerpoid)
    }
    if (query.customermagicnumber) {
      await this._prefillDealerCustomer(
        query.customermagicnumber,
        dealerSettings.autolineKeyloopEnabled
      )
    }

    if (query.companyid) {
      await this._prefillNationalCompany(query.companyid)
    }
    if (query.companymagicnumber) {
      await this._prefillDealerCompany(
        query.companymagicnumber,
        dealerSettings.autolineKeyloopEnabled
      )
    }

    if (query.leasecompany) {
      await this._prefillLeaseCompany(query.leasecompany)
    }

    if (query.leasereference) {
      await this._prefillLeaseReference(query.leasereference)
    }

    if (query.fleetcontractid) {
      let quoteId = store.getters['quote/quoteId']
      // only if there is a company
      if (store.getters['quote/hasCustomerCompany'] && query.fleetcontractid) {
        await this._prefillFleetContractId(quoteId, query.fleetcontractid)
      }
    }

    // CST-3920: a quote started from a partner (e.g. AM-I) is pushed back to the partner under its own reference
    if (query.externalReference) {
      await this._storeExternalReference(store.getters['quote/quoteId'], query.externalReference)
    }
  },
  async _storeExternalReference(quoteId, externalReference) {
    try {
      await PartnerQuote.setExternalReference(quoteId, externalReference)
    } catch (e) {
      EventBus.$emit(
        'notification-warning',
        'Could not store the external reference ' + externalReference
      )
    }
  },
  async _prefillDealerCustomer(customerId, autolineKeyloopEnabled) {
    const searchResults = await Customers.searchDealerCustomers(customerId)
    if (searchResults?.length > 0) {
      let firstSearchResult = searchResults[0]

      if (autolineKeyloopEnabled) {
        const keyloopData = await Customers.getCustomerByMagicNumber({
          magicNumber: customerId,
          type: CustomerSearchType.customer,
        })

        firstSearchResult = extend(firstSearchResult, keyloopData)
      }

      store.commit('quote/setCustomer', firstSearchResult)
      EventBus.$emit('quote/customerUpdated')
    } else {
      EventBus.$emit('notification-info', 'No customer found')
    }
  },
  async _prefillNationalCustomer(poId) {
    const searchResults = await Customers.searchNationalCustomersByPoId(poId)
    if (searchResults?.length > 0) {
      store.commit('quote/setCustomer', searchResults[0])
      EventBus.$emit('quote/customerUpdated')
    } else {
      // show message
      EventBus.$emit('notification-info', 'No customer found')
      return
    }
  },

  async _prefillNationalCompany(poId) {
    let foundCompanies = await Companies.searchNationalCompanies({
      poId,
    })

    const searchResults = foundCompanies.map((company) =>
      Companies.convertCompanyToLeaseCompanyDto(company)
    )
    if (searchResults?.length > 0) {
      store.dispatch('quote/setCustomerCompanyWithoutSaving', searchResults[0]).then(() => {
        EventBus.$emit('quote/companyUpdated')
      })
    } else {
      EventBus.$emit('notification-info', 'No company found')
    }
  },
  async _prefillDealerCompany(companyMagicNumber, autolineKeyloopEnabled) {
    const searchResults = await Companies.searchCompany(companyMagicNumber)
    if (searchResults?.length > 0) {
      let firstSearchResult = searchResults[0]

      if (autolineKeyloopEnabled) {
        const keyloopData = await Customers.getCustomerByMagicNumber({
          magicNumber: companyMagicNumber,
          type: CustomerSearchType.company,
        })

        firstSearchResult = extend(firstSearchResult, keyloopData)
      }

      store.dispatch('quote/setCustomerCompanyWithoutSaving', firstSearchResult).then(() => {
        EventBus.$emit('quote/companyUpdated')
      })
    } else {
      EventBus.$emit('notification-info', 'No company found')
    }
  },

  async _prefillLeaseCompany(leaseCompanySearchQuery) {
    const searchResults = await Companies.searchLeaseCompanies(leaseCompanySearchQuery)
    if (searchResults?.length > 0) {
      store.commit('quote/setLeaseCompany', searchResults[0])
      EventBus.$emit('quote/leaseCompanyUpdated')
    } else {
      EventBus.$emit('notification-info', 'No leasecompany found')
    }
  },

  async _prefillLeaseReference(leaseReference) {
    store.commit('quote/setLeaseReference', leaseReference)
    EventBus.$emit('quote/leaseReference')
  },

  async _prefillFleetContractId(quoteId, fleetContractId) {
    const orderDate = moment.utc().format()
    const searchResults = await FleetSalesContracts.search(orderDate, fleetContractId, quoteId)

    if (searchResults?.length > 0) {
      // call backend
      await FleetSalesContracts.setContract(orderDate, searchResults[0]?.ContractNumber, quoteId)
      store.commit('quote/setFleetsalesContract', searchResults[0])
    } else {
      EventBus.$emit('notification-info', 'No fleetcontract found')
    }
  },
}

export default PrefillService
